// -----------------------------------------------------------------------------
// engine/player.ts — the playback clock + live preview loop.
//
// This is the engine's "own clock" (CLAUDE.md §4 principle 5). A requestAnimation
// Frame loop advances the playhead while playing and composites the current frame
// every tick (so edits and scrubs show up immediately even when paused). It reads
// the store, compiles a render plan, pulls the needed frames from MediaSources,
// and draws them through the compositor. It NEVER routes rendering through the UI;
// the UI only calls play/pause/seek and listens for time updates.
// -----------------------------------------------------------------------------
import { projectDuration } from '../core/model';
import type { ProjectStore } from '../core/store';
import { Emitter } from '../shared/events';
import { clamp } from '../shared/time';
import { drawMediaLayer, drawTextLayer } from './compositor/layer';
import type { Compositor } from './compositor/gl';
import type { MediaManager } from './media/source';
import { compileRenderPlan } from './render-plan';

export interface PlayerEvents {
  time: number;
  playing: boolean;
}

export class Player {
  readonly events = new Emitter<PlayerEvents>();

  private rafId = 0;
  private playing = false;
  private currentTime = 0;
  private lastTs = 0;

  constructor(
    private readonly store: ProjectStore,
    private readonly media: MediaManager,
    private readonly compositor: Compositor,
  ) {}

  // Start the continuous render loop (called once when the editor mounts).
  mount(): void {
    if (this.rafId) return;
    this.lastTs = 0;
    this.rafId = requestAnimationFrame(this.loop);
  }
  unmount(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private loop = (ts: number): void => {
    const dt = this.lastTs ? (ts - this.lastTs) / 1000 : 0;
    this.lastTs = ts;

    if (this.playing) {
      const duration = projectDuration(this.store.state.project);
      this.currentTime += dt;
      if (this.currentTime >= duration) {
        this.currentTime = duration;
        this.setPlaying(false);
      }
      this.events.emit('time', this.currentTime);
    }

    this.renderAt(this.currentTime);
    this.rafId = requestAnimationFrame(this.loop);
  };

  // Composite a single frame at `time` into the preview canvas.
  private renderAt(time: number): void {
    const project = this.store.state.project;
    this.compositor.beginFrame(project.background);
    const textScale = this.compositor.height / project.height;
    const plan = compileRenderPlan(this.store.state, time);

    for (const layer of plan.layers) {
      if (layer.kind === 'media') {
        const source = this.media.getSource(layer.assetId);
        const bitmap = source?.peek(layer.sourceTimeSec);
        if (bitmap) drawMediaLayer(this.compositor, bitmap, layer.opacity);
      } else {
        drawTextLayer(this.compositor, layer, textScale);
      }
    }
  }

  // ---- transport -------------------------------------------------------------
  play(): void {
    if (this.playing) return;
    if (this.currentTime >= projectDuration(this.store.state.project)) this.currentTime = 0;
    this.setPlaying(true);
  }
  pause(): void {
    this.setPlaying(false);
  }
  toggle(): void {
    this.playing ? this.pause() : this.play();
  }
  seek(time: number): void {
    this.currentTime = clamp(time, 0, projectDuration(this.store.state.project));
    this.events.emit('time', this.currentTime);
  }
  getTime(): number {
    return this.currentTime;
  }
  isPlaying(): boolean {
    return this.playing;
  }

  private setPlaying(value: boolean): void {
    if (this.playing === value) return;
    this.playing = value;
    this.events.emit('playing', value);
  }
}
